import { Box, createStyles, Divider, Grid, Tab, Tabs } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Card, Icons, Typography } from '@nutrien/cxp-components';
import React from 'react';

import ViewAgendaIcon from '../../assets/icons/viewAgendaIcon';
import { useMst } from '../../mobx-models/Root';
import { DeficiencyView } from '../../models/models';
import { setValueinLocalStorage } from '../../utilities/utilityFunctions';

interface Props {
  isSortByOpen: boolean;
  id: string;
  handleSortByClick: (e: React.MouseEvent<HTMLElement>) => void;
  view: DeficiencyView;
  setView: React.Dispatch<React.SetStateAction<DeficiencyView>>;
  tabValue: number;
  setValue: (tabValue: number) => void;
  resetSortBy: () => void;
  activeDeficiencyCount: number;
  inspectionDeficiencyCount: number;
}

const useStyles = makeStyles(() =>
  createStyles({
    sortByPanelOuterDiv: {
      '& .sortByPanelInnerCard': {
        padding: '8px 16px',
        marginBottom: '0px',
      },
    },
    sortByGrid: {
      placeSelf: 'center',
    },
    sortByText: {
      cursor: 'pointer',
      padding: 0,
    },
    sortByIcon: {
      verticalAlign: 'middle',
      marginLeft: 10,
    },
    dividerGridContainer: {
      marginLeft: 16,
      marginRight: 16,
      height: 32,
      alignSelf: 'center',
    },
    dividerWidth: {
      backgroundColor: 'rgba(255, 255, 255, 0.4)',
      width: 0.5,
    },
    condensedViewIcon: {
      cursor: 'pointer',
      textAlign: 'center',
      height: 36,
      width: 36,
      borderRadius: '4px 0px 0px 4px',
      backgroundColor: 'rgba(255, 255, 255, 0.09)',
    },
    cardViewIcon: {
      cursor: 'pointer',
      textAlign: 'center',
      height: 36,
      width: 36,
      borderRadius: '0px 4px 4px 0px',
      backgroundColor: 'rgba(255, 255, 255, 0.09)',
      marginLeft: 2,
    },
    viewImage: {
      width: '24px',
      height: '-webkit-fill-available',
    },
    tabs: {
      paddingTop: 0,
      paddingBottom: 0,
      whiteSpace: 'nowrap',
      maxWidth: '320px',
      minWidth: '250px',
    },
    thinOutline: {
      '& path': {
        strokeWidth: 0.5,
      },
    },
  }),
);

const DeficienciesSortByPanel: React.FC<Props> = ({
  isSortByOpen,
  id,
  handleSortByClick,
  view,
  setView,
  setValue,
  tabValue,
  resetSortBy,
  activeDeficiencyCount,
  inspectionDeficiencyCount,
}) => {
  const classes = useStyles();
  const { CurrentUserSelected } = useMst();
  const deficiencyCountBasedOnTab =
    tabValue === 0 ? inspectionDeficiencyCount : activeDeficiencyCount;

  const changeTab = (event: React.ChangeEvent<unknown>, newValue: number) => {
    setValueinLocalStorage(
      CurrentUserSelected.isAdhoc ? 'adhocDeficiencyTabValue' : 'deficiencyTabValue',
      String(newValue),
    );
    setValue(newValue);
    resetSortBy();
  };

  return (
    <div className={classes.sortByPanelOuterDiv}>
      <Card elevation={2} className="sortByPanelInnerCard">
        <Grid item container xs={12} justifyContent="space-between">
          <Grid item container xs={7}>
            <Box sx={{ borderColor: 'divider' }}>
              <Tabs
                value={tabValue}
                onChange={changeTab}
                aria-label="basic tabs example"
                textColor="primary"
                indicatorColor="primary"
              >
                <Tab
                  label={`Inspection Deficiencies (${inspectionDeficiencyCount})`}
                  className={classes.tabs}
                />
                <Tab
                  label={`Active Deficiencies for Asset (${activeDeficiencyCount})`}
                  className={classes.tabs}
                />
              </Tabs>
            </Box>
          </Grid>
          {deficiencyCountBasedOnTab > 0 && (
            <Grid item container xs={5} justifyContent="flex-end">
              <Grid
                aria-describedby={id}
                id="sort-by-deficiency"
                item
                className={classes.sortByGrid}
                onClick={handleSortByClick}
              >
                <Typography
                  color="primary"
                  variant="subtitle1"
                  fontWeight={700}
                  className={classes.sortByText}
                >
                  Sort By
                  {isSortByOpen ? (
                    <Icons.ChevronUpFeather color="primary" className={classes.sortByIcon} />
                  ) : (
                    <Icons.ChevronDownFeather color="primary" className={classes.sortByIcon} />
                  )}
                </Typography>
              </Grid>
              <Grid item className={classes.dividerGridContainer}>
                <Divider orientation="vertical" className={classes.dividerWidth} />
              </Grid>
              <Grid
                data-testid="def-condensed-view"
                onClick={() => setView({ condensed: true, card: false })}
                item
                className={classes.condensedViewIcon}
              >
                <Icons.MenuFeather
                  color={view.condensed ? 'primary' : 'currentColor'}
                  className={classes.viewImage}
                />
              </Grid>
              <Grid
                data-testid="def-card-view"
                onClick={() => setView({ condensed: false, card: true })}
                item
                className={classes.cardViewIcon}
              >
                <ViewAgendaIcon
                  className={`${classes.viewImage}`}
                  color={view.card ? 'primary' : 'inherit'}
                />
              </Grid>
            </Grid>
          )}
        </Grid>
      </Card>
    </div>
  );
};

export default DeficienciesSortByPanel;
